import React from "react";
import { useNavigate, useLocation } from "react-router-dom";

// Dedicated "Payment Success" page.
//
// Nothing in the app navigates here yet — this is scaffolding only. Once the
// real payment gateway response can be connected (see cyberGatewayService.js
// and PaymentMethod.jsx), whatever confirms a successful payment should
// navigate here with the real details, e.g.:
//   navigate("/payment-success", { state: { amount, reference, email, name } });
// and this page will display them. Until then it renders with generic
// messaging and no data.
//
// Deliberately contains NO simulated/mocked "success" logic and NO automatic
// redirect — the visitor continues by clicking the button themselves.
//
// Purely visual component below this point: entrance/checkmark animations use
// `motion-safe:` so they no-op under prefers-reduced-motion, and are plain
// CSS (Tailwind keyframes in tailwind.config.js) rather than a JS animation
// library, so there's no runtime performance cost.

// One label/value row in the details card. Renders nothing if `value` is
// empty, so the card degrades gracefully field-by-field.
const DetailRow = ({ icon, label, value, mono, delayMs }) => {
  if (!value) return null;
  return (
    <div
      className="motion-safe:animate-fade-in-up flex items-center gap-3 py-3 first:pt-0 last:pb-0"
      style={{ animationDelay: `${delayMs}ms` }}
    >
      <span className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
        {icon}
      </span>
      <div className="min-w-0 flex-1 flex items-center justify-between gap-3">
        <span className="text-xs sm:text-sm text-gray-500">{label}</span>
        <span
          className={`text-sm sm:text-base font-semibold text-gray-800 truncate max-w-[60%] text-right ${
            mono ? "font-mono tracking-tight" : ""
          }`}
        >
          {value}
        </span>
      </div>
    </div>
  );
};

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const location = useLocation();

  // Optional details the eventual real integration can pass in via router
  // state. All are optional — the page renders sensibly with none of them.
  const { amount, reference, email, name } = location.state || {};
  const amountLabel = amount != null ? `LKR ${Number(amount).toLocaleString()}` : null;
  const hasDetails = Boolean(amountLabel || reference || email || name);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 sm:py-16 bg-gradient-to-br from-blue-50 via-white to-blue-100">
      <div className="w-full max-w-md sm:max-w-lg">
        <div className="motion-safe:animate-scale-in bg-white rounded-3xl shadow-2xl ring-1 ring-black/5 p-6 sm:p-9 md:p-10 text-center">
          {/* Success icon — glow ring behind an animated circle + checkmark draw-in */}
          <div className="relative mx-auto mb-5 sm:mb-6 w-20 h-20 sm:w-24 sm:h-24">
            <span className="absolute inset-0 rounded-full bg-green-400/20 motion-safe:animate-ping [animation-iteration-count:1] [animation-duration:0.9s]" />
            <span className="absolute inset-0 rounded-full bg-gradient-to-br from-green-50 to-emerald-100" />
            <svg viewBox="0 0 64 64" className="relative w-full h-full">
              <circle
                cx="32"
                cy="32"
                r="28"
                fill="none"
                stroke="#16a34a"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray="176"
                className="motion-safe:animate-draw-circle"
              />
              <path
                d="M19 33 L27.5 41.5 L45 22"
                fill="none"
                stroke="#16a34a"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="48"
                className="motion-safe:animate-draw-check"
              />
            </svg>
          </div>

          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
            Payment Successful
          </h1>

          {hasDetails && (
            <div className="motion-safe:animate-fade-in-up bg-gray-50/80 rounded-2xl px-4 sm:px-5 py-1 mt-6 sm:mt-7 text-left divide-y divide-gray-100">
              <DetailRow
                delayMs={80}
                label="Payer"
                value={name}
                icon={
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                }
              />
              <DetailRow
                delayMs={140}
                label="Amount paid"
                value={amountLabel}
                icon={
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.66 0-3 .9-3 2s1.34 2 3 2 3 .9 3 2-1.34 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V6m0 10v2m9-6a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                }
              />
              <DetailRow
                delayMs={200}
                label="Reference"
                value={reference}
                mono
                icon={
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                }
              />
              <DetailRow
                delayMs={260}
                label="Email"
                value={email}
                icon={
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                }
              />
            </div>
          )}

          <button
            onClick={() => navigate("/", { replace: true })}
            className="motion-safe:animate-fade-in-up group w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-700 to-blue-800 hover:from-blue-800 hover:to-blue-900 active:from-blue-900 active:to-blue-950 text-white font-semibold py-3.5 rounded-xl text-sm sm:text-base mt-7 sm:mt-8 transition-all shadow-sm hover:shadow-lg hover:-translate-y-0.5"
            style={{ animationDelay: "320ms" }}
          >
            Back to Login
            <svg
              className="w-4 h-4 transition-transform group-hover:translate-x-0.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        <p className="motion-safe:animate-fade-in-up text-center text-xs text-gray-400 mt-5" style={{ animationDelay: "380ms" }}>
          Sports Facility Management System · University of Sri Jayewardenepura
        </p>
      </div>
    </div>
  );
}
